import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Mail, Eye, Trash2, Archive, X, Send } from 'lucide-react';
import { contactsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function AdminContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadContacts(); }, [filter]);

  const loadContacts = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await contactsService.getAll(params);
      setContacts(response.data.contacts || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const viewContact = async (contact) => {
    try {
      const response = await contactsService.getById(contact.id);
      setSelectedContact(response.data.contact);
      loadContacts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleArchive = async (id) => {
    try {
      await contactsService.update(id, { is_archived: true });
      toast.success('Archivé');
      loadContacts();
      setSelectedContact(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ?')) return;
    try {
      await contactsService.delete(id);
      toast.success('Supprimé');
      loadContacts();
      setSelectedContact(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><LoadingSpinner size="lg" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Messages</h1>
          <p className="text-dark-500">Gérez les demandes de contact</p>
        </div>
        <div className="flex gap-2">
          {['all', 'unread', 'read', 'archived'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`badge cursor-pointer ${filter === f ? 'badge-primary' : 'bg-dark-100 text-dark-600'}`}>
              {f === 'all' ? 'Tous' : f === 'unread' ? 'Non lus' : f === 'read' ? 'Lus' : 'Archivés'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 card divide-y divide-dark-100 max-h-[calc(100vh-250px)] overflow-y-auto">
          {contacts.length === 0 ? (
            <div className="p-8 text-center text-dark-500">Aucun message</div>
          ) : (
            contacts.map((contact) => (
              <div key={contact.id} onClick={() => viewContact(contact)} className={`p-4 cursor-pointer hover:bg-dark-50 transition-colors ${selectedContact?.id === contact.id ? 'bg-primary-50' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-medium text-dark-900 truncate">{contact.name}</p>
                  {!contact.is_read && <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />}
                </div>
                <p className="text-sm text-dark-600 truncate">{contact.subject || 'Sans objet'}</p>
                <p className="text-xs text-dark-400 mt-1">{format(new Date(contact.created_at), 'd MMM, HH:mm', { locale: fr })}</p>
              </div>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {selectedContact ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-dark-900">{selectedContact.subject || 'Sans objet'}</h2>
                  <p className="text-dark-500">De: {selectedContact.name} ({selectedContact.email})</p>
                  {selectedContact.phone && <p className="text-dark-500">Tél: {selectedContact.phone}</p>}
                  {selectedContact.company && <p className="text-dark-500">Entreprise: {selectedContact.company}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleArchive(selectedContact.id)} className="p-2 hover:bg-dark-100 rounded-lg text-dark-500" title="Archiver">
                    <Archive className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(selectedContact.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Supprimer">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="bg-dark-50 rounded-xl p-4 mb-6">
                <p className="text-dark-700 whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              <div className="text-sm text-dark-400">
                Reçu le {format(new Date(selectedContact.created_at), 'd MMMM yyyy à HH:mm', { locale: fr })}
              </div>
              <div className="mt-6 pt-6 border-t">
                <a href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject || 'Votre message'}`} className="btn-primary">
                  <Send className="w-4 h-4 mr-2" />Répondre par email
                </a>
              </div>
            </motion.div>
          ) : (
            <div className="card p-12 text-center">
              <Mail className="w-12 h-12 text-dark-300 mx-auto mb-4" />
              <p className="text-dark-500">Sélectionnez un message pour le lire</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminContactsPage;
